import { nanoid } from 'nanoid';
import * as React from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { getFormValues } from 'redux-form';

import type { FormJobOutletContext } from 'af-root/scenes/Company/Jobs/Form/types';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import { PlainUnitEnum } from '@acceligentllc/shared/enums/quantityUnit';

import type { RootState } from 'af-reducers';

import { useToggle } from 'af-utils/react.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import withOutletContext from 'af-components/outletContextWrapper';

import CLIENT from 'af-routes/client';

import { formSectionClass } from '../helpers';
import BulkImport from './BulkImport';
import styles from '../styles.module.scss';
import type { BillingCodeFM } from './BillingCodes/BillingCodeArray';
import BillingCodeArray from './BillingCodes/BillingCodeArray';
import BillingCodeGroupsArray from './BillingCodes/BillingCodeGroupsArray';
import moduleStyles from './styles.module.scss';
import type JobUpsertFM from '../formModel';
import type { BillingCodeGroupFM } from '../formModel';
import type ImportBillingCodeFM from './BulkImport/formModel';

type OwnProps = FormJobOutletContext;

type Props = ConnectedProps<typeof connector> & InjectedFormProps<JobUpsertFM>;

export type NormalizedImport = ImportBillingCodeFM & {
	resolvedGroupKey: Nullable<string>;
	resolvedGroupId: Nullable<number>;
	resolvedGroupName: Nullable<string>;
	resolvedGroupColor: Nullable<ColorPalette>;
};

const BillingCodesTab: React.FC<Props> = (props) => {
	const {
		billingCodes,
		billingCodeGroups,
		change,
		hasPermissionsToManageBillingCodes,
	} = props;

	const [importedBillingCodes, setImportedBillingCodes] = React.useState<NormalizedImport[]>([]);
	const history = useHistory();
	const location = useLocation<{ orgAlias: string; }>();

	const {
		value: showBulkImportModal,
		setToTrue: openBulkImportModal,
		setToFalse: hideBulkImportModal,
	} = useToggle(false);

	if (!hasPermissionsToManageBillingCodes) {
		history.push(CLIENT.ERROR.ERR403(location.state.orgAlias));
	}

	const { lineItemNumberCounter, customerIdCounter } = React.useMemo(() => {
		const _lineItemNumberCounter: Record<number, number> = {};
		const _customerIdCounter: Record<string, number> = {};
		let _hasDuplicates = false;
		if (billingCodes?.length) {
			for (const billingCode of billingCodes) {
				if (billingCode.lineItemNumber) {
					_lineItemNumberCounter[billingCode.lineItemNumber] = (_lineItemNumberCounter[billingCode.lineItemNumber] ?? 0) + 1;
					if (_lineItemNumberCounter[billingCode.lineItemNumber] > 1) {
						_hasDuplicates = true;
					}
				}
				if (billingCode.customerId) {
					_customerIdCounter[billingCode.customerId] = (_customerIdCounter[billingCode.customerId] ?? 0) + 1;

					if (_customerIdCounter[billingCode.customerId] > 1) {
						_hasDuplicates = true;
					}
				}
			}
		}

		return {
			lineItemNumberCounter: _lineItemNumberCounter,
			customerIdCounter: _customerIdCounter,
			hasDuplicates: _hasDuplicates,
		};
	}, [billingCodes]);

	function resolveGroupsForImport(
		incoming: ImportBillingCodeFM[],
		existingGroups: BillingCodeGroupFM[]
	): { nextGroups: BillingCodeGroupFM[]; normalized: NormalizedImport[]; } {

		const existingGroupList = existingGroups;

		const initialAccumulator = {
			nextGroups: [...existingGroupList],
			byName: existingGroupList.reduce<Record<string, BillingCodeGroupFM>>((groupsByName, group) => {
				const trimmedName = group.name.trim();
				if (trimmedName) {
					groupsByName[trimmedName] = group;
				}
				return groupsByName;
			}, {}),
			normalized: [] as NormalizedImport[],
		};

		const result = incoming.reduce((accumulator, billingCode) => {
			const trimmedIncomingName = billingCode.group?.trim();

			if (!trimmedIncomingName) {
				accumulator.normalized.push({
					...billingCode,
					resolvedGroupKey: null,
					resolvedGroupId: null,
					resolvedGroupName: null,
					resolvedGroupColor: null,
				});
				return accumulator;
			}

			const groupNameKey = trimmedIncomingName;
			let resolvedGroup = accumulator.byName[groupNameKey];

			if (!resolvedGroup) {
				resolvedGroup = {
					id: nanoid(),
					databaseId: null,
					name: trimmedIncomingName,
					color: null,
					sortIndex: accumulator.nextGroups.length + 1,
					deletedAt: null,
				};

				accumulator.nextGroups.push(resolvedGroup);
				accumulator.byName[groupNameKey] = resolvedGroup;
			}

			accumulator.normalized.push({
				...billingCode,
				unit: (billingCode.unit as string) === 'LF' ? PlainUnitEnum.LFT : billingCode.unit,
				resolvedGroupKey: `${resolvedGroup.id}`,
				resolvedGroupId: resolvedGroup.databaseId ?? null,
				resolvedGroupName: resolvedGroup.name,
				resolvedGroupColor: resolvedGroup.color,
			});

			return accumulator;
		}, initialAccumulator);

		return {
			nextGroups: result.nextGroups,
			normalized: result.normalized,
		};
	}

	React.useEffect(() => {
		// groupId -> true lookup
		const allowedById = billingCodeGroups.reduce<Record<string, true>>((acc, group) => {
			acc[`${group.id}`] = true;
			return acc;
		}, {});

		let hasChanges = false;

		const nextCodes = billingCodes.map((billingCode) => {
			const groupKey = billingCode.groupKey;

			if (!groupKey) {
				return billingCode;
			}

			if (!allowedById[groupKey]) {
				hasChanges = true;
				return { ...billingCode, groupKey: null, groupId: null, group: null };
			}

			return billingCode;
		});

		if (hasChanges) {
			change('billingCodes', nextCodes);
		}
	}, [billingCodeGroups, billingCodes, change]);

	const handleChangeBillingCodeGroups = React.useCallback((nextGroups: BillingCodeGroupFM[]) => {
		change('billingCodeGroups', nextGroups);
	}, [change]);

	const handleChangeBillingCodes = React.useCallback((next: BillingCodeFM[]) => {
		change('billingCodes', next);
	}, [change]);

	const handleAddImportedBillingCodes = React.useCallback((incoming: ImportBillingCodeFM[]) => {
		const { nextGroups, normalized } = resolveGroupsForImport(incoming, billingCodeGroups);

		change('billingCodeGroups', nextGroups);
		setImportedBillingCodes(normalized);
	}, [billingCodeGroups, change]);

	return (
		<div className={styles['job-form__tab-content']} >
			<div className={getFormSectionClass()}>
				<BillingCodeGroupsArray
					initialGroups={billingCodeGroups}
					onChange={handleChangeBillingCodeGroups}
				/>
			</div>

			<div className={getFormSectionClass()}>
				<BillingCodeArray
					billingCodeGroups={billingCodeGroups}
					customerIdCounter={customerIdCounter}
					importedBillingCodes={importedBillingCodes}
					initialBillingCodes={billingCodes}
					lineItemNumberCounter={lineItemNumberCounter}
					onChange={handleChangeBillingCodes}
					openBulkImportModal={openBulkImportModal}
				/>
				<BulkImport
					addImportedBillingCodes={handleAddImportedBillingCodes}
					closeModal={hideBulkImportModal}
					showModal={showBulkImportModal}
				/>
			</div>
		</div>
	);

	function getFormSectionClass() {
		return `${moduleStyles['form-section-stacking-context']} ${formSectionClass}`;
	}
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { user: { companyData, userData } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const hasPermissionsToManageBillingCodes = isAllowed(
		PagePermissions.COMPANY.JOBS.MANAGE_BILLING_CODES,
		permissions,
		isCompanyAdmin,
		role
	);

	const currentFormValues = getFormValues(ownProps.formName)(state) as JobUpsertFM;

	return {
		hasPermissionsToManageBillingCodes,
		billingCodes: currentFormValues?.billingCodes ?? [],
		billingCodeGroups: currentFormValues?.billingCodeGroups ?? [],
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType>(
	withOutletContext<FormJobOutletContext, undefined>,
	connector,
	React.memo
);

export default enhance(BillingCodesTab);
